import { ChangeEvent, MouseEvent, useState } from "react";
import { format } from "date-fns";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { InventoryItemDto } from "types/api/items";
import { getFnsLocale } from "../../../translations/date-fns-locale";
import {
  extractConsolidationId,
  formatConsolidationSku,
} from "../../../utils/consolidationSku";
import { getItemStatusGroup } from "../itemStatusGroups";

export interface InventoryItemChange {
  isSelected: boolean;
  selectedQuantity: any;
}

interface UseInventoryItemArgs {
  inventoryItem: any;
  isItemInCart: any;
  isSelected: boolean;
  onChange: (change: InventoryItemChange) => void;
}

/**
 * Selection, quantity and derived display fields shared by the two renderings
 * of a storage item — InventoryItemRow and InventoryItemTile. Every read of a
 * field only one item endpoint sends happens here; rendering-only concerns
 * (columns, photos, how the consolidation link looks) stay in the components.
 */
const useInventoryItem = ({
  inventoryItem,
  isItemInCart,
  isSelected,
  onChange,
}: UseInventoryItemArgs) => {
  const { i18n } = useTranslation("common");
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const item: InventoryItemDto = inventoryItem.toJS();
  const pictures = item.pictures;
  const hasPictures = pictures && pictures.length > 0;
  const weight = item.weight && Number(item.weight).toFixed(2);
  const comment = item.customer_comment;
  const itemAddons = inventoryItem.get("addons", List());

  const createdAt = inventoryItem.get("created_at", new Date());
  const locale = getFnsLocale(i18n.language);
  const currentYear = new Date().getFullYear();
  const created = format(new Date(createdAt), "dd MMMM, y", {
    locale,
  }).split(", ");
  const parcelDate =
    currentYear === parseFloat(created[1]) ? created[0] : created.join(", ");

  const isDecantItem = isItemInCart; //TODO: FIXME: Decant item will have SKU starting with letter "Q"
  const isCartableItem =
    ["Active", "InContainer", "ContainerPacked"].indexOf(
      inventoryItem.get("status"),
    ) !== -1;
  const quantity =
    ("decant_quantity" in item && item.decant_quantity) || item.quantity;
  const hasQuantity = quantity > 1;

  // Only the shipment drill-down (/shipments/:id) says where an item sits.
  const location =
    ("container" in item && item.container?.sku) ||
    ("location" in item && item.location) ||
    null;

  // The drill-down nests the consolidation; the items search (/items) sends
  // only its id, plus the parent shipment the drill-down leaves out.
  const consolidationId = extractConsolidationId(
    ("consolidation" in item && item.consolidation?.sku) ||
      ("consolidation_id" in item && item.consolidation_id) ||
      null,
  );
  const shipmentId = "shipment_id" in item ? item.shipment_id : undefined;
  const consolidationLink =
    consolidationId === null
      ? null
      : {
          sku: formatConsolidationSku(consolidationId),
          // A shipped item's consolidation is listed in History, not Outgoing.
          to:
            getItemStatusGroup(item.status) === "Shipped"
              ? "/parcels/history"
              : "/parcels/outgoing",
          state: { consolidationId, shipmentId },
        };

  // The checkbox input only swallows the event; selection toggles on the
  // surrounding checkbox click area.
  const handleCheckBoxClick = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleCheckboxAreaClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isItemInCart && isCartableItem) {
      onChange({
        isSelected: !isSelected,
        selectedQuantity,
      });
    }
  };

  const handleSetQuantity = (nextValue: number) => {
    const valueToSet =
      isNaN(nextValue) || nextValue > quantity || nextValue < 1
        ? selectedQuantity
        : nextValue;
    setSelectedQuantity(valueToSet);
    onChange({
      isSelected,
      selectedQuantity: valueToSet,
    });
  };

  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValueParsed = parseInt(event.target.value);
    handleSetQuantity(nextValueParsed);
  };

  const handleIncrease = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    handleSetQuantity(selectedQuantity + 1);
  };

  const handleDecrease = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    handleSetQuantity(selectedQuantity - 1);
  };

  const preventInputEvents = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return {
    item,
    pictures,
    hasPictures,
    weight,
    comment,
    itemAddons,
    parcelDate,
    isDecantItem,
    isCartableItem,
    quantity,
    hasQuantity,
    location,
    consolidationLink,
    selectedQuantity,
    handleCheckBoxClick,
    handleCheckboxAreaClick,
    handleQuantityChange,
    handleIncrease,
    handleDecrease,
    preventInputEvents,
  };
};

export default useInventoryItem;
